import * as THREE from 'three';
import { VoxelGrid } from '../core/voxel-grid.js';

// ---------------------------------------------------------------------------
// The fish market: a jetty on the seaward shore where a catch turns into
// money. It exists so the island is somewhere you sail TO rather than
// scenery you sail past — and so the round trip ends where the links are.
// ---------------------------------------------------------------------------

export function registerMarketBlocks(palette) {
    return {
        plank: palette.add('jettyPlank', 0x9a7a4e, { jitter: 0.09 }),
        post: palette.add('jettyPost', 0x5e4630, { jitter: 0.08 }),
        crate: palette.add('jettyCrate', 0xb08b53, { jitter: 0.07 }),
        cloth: palette.add('jettyCloth', 0xc7503f, { jitter: 0.06 }),
        lamp: palette.add('jettyLamp', 0xffc27a, { emissive: 1.0, jitter: 0.02 }),
    };
}

/** Local +X runs out to sea; the landward end is at x = 0. */
/**
 * @param opts.length how far the jetty reaches out, in voxels. It has to be
 *   scaled to the harbour it stands in: a jetty as long as the basin's radius
 *   reaches the middle and cuts the water in half.
 */
export function createMarket(palette, material, blocks, { voxelSize, length = 20 }) {
    const P = blocks;
    const LEN = length;
    const W = 7;
    const g = new VoxelGrid(LEN, 12, W);
    const cz = (W - 1) >> 1;

    // Decking, with posts running down past the waterline. The deck stands
    // clear of the swell, so the posts are longer than they look.
    g.box(0, 3, cz - 1, LEN - 1, 3, cz + 1, P.plank);
    for (let x = 1; x < LEN; x += 4) {
        for (const z of [cz - 1, cz + 1]) g.box(x, -3, z, x, 2, z, P.post);
    }

    // A stall at the landward end: crates, a counter, an awning.
    g.box(1, 4, cz - 1, 3, 5, cz + 1, P.crate);
    g.box(1, 6, cz - 1, 4, 6, cz + 1, P.cloth);
    g.box(5, 4, cz - 1, 5, 4, cz + 1, P.crate);

    // Bollards at the seaward end to tie up to, and a lamp above them.
    g.box(LEN - 2, 4, cz - 1, LEN - 2, 5, cz - 1, P.post);
    g.box(LEN - 2, 4, cz + 1, LEN - 2, 5, cz + 1, P.post);
    g.box(LEN - 6, 4, cz, LEN - 6, 8, cz, P.post);
    g.set(LEN - 6, 9, cz, P.lamp);

    const mesh = new THREE.Mesh(
        g.build(palette, {
            voxelSize,
            origin: new THREE.Vector3(0, -3 * voxelSize, -cz * voxelSize),
        }),
        material,
    );
    const group = new THREE.Group();
    group.add(mesh);
    group.name = 'market';
    return { group, length: LEN * voxelSize, halfWidth: (W / 2) * voxelSize };
}
